/**
 * Prefetch + cache Jellyfin web embed credentials (serverId + webSession) so the first
 * "Open in web view" / embed play does not wait on /api/library/jellyfin-context.
 */

const CACHE_KEY = 'zflix_jf_ctx_v1';
const CACHE_MS = 45 * 60 * 1000;

let memory = null;

function readSessionStorage() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const j = JSON.parse(raw);
    if (!j?.serverId || !j.fetchedAt) return null;
    if (Date.now() - j.fetchedAt > CACHE_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return j;
  } catch {
    return null;
  }
}

function writeSessionStorage(entry) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch (_) {}
}

export function clearZflixJellyfinContextCache() {
  memory = null;
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch (_) {}
}

export function peekZflixJellyfinContext() {
  if (memory && Date.now() - memory.fetchedAt <= CACHE_MS) return memory;
  const stored = readSessionStorage();
  if (stored) memory = stored;
  return memory;
}

/**
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<{ serverId: string|null, webSession: object|null, error: string|null, fetchedAt: number }>}
 */
export async function fetchZflixJellyfinContext(opts = {}) {
  const force = !!opts.force;
  if (!force) {
    const hit = peekZflixJellyfinContext();
    if (hit?.serverId) return hit;
  }

  const r = await fetch('/api/library/jellyfin-context', { credentials: 'include' });
  const j = await r.json().catch(() => ({}));
  const entry = {
    serverId: r.ok && j.serverId ? String(j.serverId) : null,
    webSession: j.webSession || null,
    error: r.ok ? null : j.error || 'Could not load Jellyfin context',
    fetchedAt: Date.now(),
  };
  if (entry.serverId) {
    memory = entry;
    writeSessionStorage(entry);
  }
  return entry;
}

/** Warm cache after Zflix site login / on app load. */
export function prefetchZflixJellyfinContext() {
  fetchZflixJellyfinContext().catch(() => {});
}
